import { ChangeDetectionStrategy, Component, computed, input, signal, type OnInit } from '@angular/core';

/**
 * Build metadata, shared by the API health response (`version`) and the SPA image's `/build-info.json`.
 */
interface BuildInfo {
  sha?: string;
  builtAt?: string;
}

/**
 * Corner indicator of where the app really runs: the frontend origin and, probed live from the
 * BFF health endpoint, where `/api` resolves — the two can differ in local dev — each with the
 * build it runs (short sha + build time; full values on hover). Hidden in production.
 */
@Component({
  selector: 'esn-env-badge',
  templateUrl: './env-badge.component.html',
  styleUrl: './env-badge.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnvBadgeComponent implements OnInit {

  /**
   * Frontend serving origin from `environment.source` (cli | local | aws-dev | aws-stage | prod).
   */
  readonly frontend = input.required<string>();

  /**
   * Health endpoint to probe for the API's runtime and build.
   */
  readonly apiHealthPath = input('/api');

  /**
   * The SPA image's own build metadata, written by spa.Dockerfile.
   */
  readonly buildInfoPath = input('/build-info.json');

  protected readonly apiSource = signal('…');
  protected readonly uiVersion = signal<BuildInfo>({});
  protected readonly apiVersion = signal<BuildInfo>({});

  protected readonly visible = computed(() => {
    const fe = this.frontend().toLowerCase();
    return fe !== '' && fe !== 'prod' && fe !== 'production';
  });

  protected tone(source: string): string {
    const s = source.toLowerCase();
    if (s === 'cli') return 'info';
    if (s === 'local') return 'local';
    if (s.startsWith('aws') || s === 'dev' || s === 'stage') return 'warning';
    return 'neutral';
  }

  /**
   * Short git sha for the chip; the full one lives in the hover title.
   */
  protected sha(v: BuildInfo): string {
    return v.sha ? v.sha.slice(0, 7) : '';
  }

  /**
   * Compact UTC build time (YYYY-MM-DD HH:MM); '' when unknown or unparseable.
   */
  protected built(v: BuildInfo): string {
    if (!v.builtAt) return '';
    const d = new Date(v.builtAt);
    if (Number.isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
  }

  protected title(label: string, v: BuildInfo): string {
    if (!v.sha && !v.builtAt) return '';
    return `${label} ${v.sha ?? ''}${v.builtAt ? ` · ${v.builtAt}` : ''}`.trim();
  }

  async ngOnInit(): Promise<void> {
    if (!this.visible()) return;
    void this.loadUiVersion();
    try {
      const res = await fetch(this.apiHealthPath(), { headers: { accept: 'application/json' } });
      const body = (await res.json()) as { runtime?: string; version?: BuildInfo };
      this.apiSource.set(typeof body?.runtime === 'string' ? body.runtime : 'unknown');
      if (body?.version) this.apiVersion.set(body.version);
    } catch {
      this.apiSource.set('unreachable');
    }
  }

  private async loadUiVersion(): Promise<void> {
    try {
      const res = await fetch(this.buildInfoPath(), { headers: { accept: 'application/json' } });
      if (res.ok) this.uiVersion.set((await res.json()) as BuildInfo);
    } catch {
      // No build-info.json (e.g. `nx serve`) — leave the UI version blank.
    }
  }
}
